// src/admin/ManageStock.jsx
import { useState, useEffect } from "react";
import { getAllGames } from "../services/gameService";
import { addAccount, bulkAddAccounts } from "../services/accountStockService";

const S = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

.ms-grid { display:grid; grid-template-columns:1fr 1fr; gap:18px; }

.ms-card {
  background:#1a1a1a; border:1px solid rgba(255,255,255,0.08);
  border-radius:10px; padding:22px;
}
.ms-card-title { font-size:14px; font-weight:700; color:#fff; margin-bottom:16px; }

.ms-inp {
  width:100%; background:rgba(255,255,255,0.05);
  border:1px solid rgba(255,255,255,0.08); border-radius:6px;
  padding:10px 12px; color:#fff;
  font-family:'Inter',sans-serif; font-size:13px; outline:none;
  transition:border-color 0.15s; margin-bottom:12px;
}
.ms-inp:focus { border-color:rgba(0,120,242,0.4); }
.ms-lbl {
  display:block; font-size:10px; font-weight:700; color:#6b6b6b;
  letter-spacing:1px; text-transform:uppercase; margin-bottom:6px;
}

.ms-tabs { display:flex; gap:6px; margin-bottom:16px; }
.ms-tab {
  padding:7px 16px; border-radius:6px; font-size:12px; font-weight:600;
  cursor:pointer; transition:all 0.12s; border:1px solid;
  background:none; font-family:'Inter',sans-serif;
}
.ms-tab.on  { background:rgba(0,120,242,0.1); border-color:rgba(0,120,242,0.3); color:#0078F2; }
.ms-tab.off { border-color:rgba(255,255,255,0.08); color:#6b6b6b; }

.ms-btn {
  padding:11px 22px; background:#0078F2; border:none; border-radius:6px;
  color:#fff; font-family:'Inter',sans-serif; font-size:13px; font-weight:700;
  cursor:pointer; transition:all 0.15s; width:100%;
}
.ms-btn:hover:not(:disabled) { background:#0a84ff; }
.ms-btn:disabled { opacity:0.4; cursor:not-allowed; }

.ms-stock-row {
  background:rgba(255,255,255,0.02); border:1px solid rgba(255,255,255,0.06);
  border-radius:8px; padding:12px 14px; margin-bottom:8px;
  display:flex; align-items:center; justify-content:space-between; gap:12px;
}
.ms-success {
  background:rgba(34,197,94,0.08); border:1px solid rgba(34,197,94,0.2);
  border-radius:6px; padding:11px 14px; color:#22c55e;
  font-size:13px; margin-bottom:14px; font-weight:500;
}
.ms-note {
  margin-top:14px; padding:12px 14px;
  background:rgba(234,179,8,0.06); border:1px solid rgba(234,179,8,0.15);
  border-radius:6px; font-size:12px; color:#eab308; line-height:1.6;
}

/* ════════════════════
   MOBILE ≤ 768px
════════════════════ */
@media(max-width:768px) {
  .ms-grid { grid-template-columns:1fr; gap:14px; }
  .ms-card { padding:16px; }
  .ms-btn  { padding:12px; }
}
`;

export default function ManageStock() {
  const [games,         setGames]         = useState([]);
  const [selectedGame,  setSelectedGame]  = useState("");
  const [loading,       setLoading]       = useState(true);
  const [mode,          setMode]          = useState("single");
  const [email,         setEmail]         = useState("");
  const [password,      setPassword]      = useState("");
  const [note,          setNote]          = useState("");
  const [saving,        setSaving]        = useState(false);
  const [success,       setSuccess]       = useState("");
  const [csvText,       setCsvText]       = useState("");

  const reload = () => getAllGames().then(g => setGames(g.filter(x => x.active)));
  useEffect(() => { reload().then(() => setLoading(false)); }, []);

  async function handleAddSingle(e) {
    e.preventDefault();
    if (!selectedGame || !email || !password) return;
    setSaving(true); setSuccess("");
    try {
      await addAccount(selectedGame, email.trim(), password.trim(), note.trim());
      setSuccess("✅ Account added! Stock updated.");
      setEmail(""); setPassword(""); setNote("");
      reload();
    } catch (err) { alert("Error: " + err.message); }
    finally { setSaving(false); }
  }

  async function handleBulkAdd(e) {
    e.preventDefault();
    if (!selectedGame || !csvText.trim()) return;
    setSaving(true); setSuccess("");
    try {
      const rows = csvText.trim().split("\n").map(line => {
        const [steamEmail, steamPassword, noteVal] = line.split(",").map(s => s?.trim() || "");
        return { steamEmail, steamPassword, note:noteVal };
      }).filter(r => r.steamEmail && r.steamPassword);
      if (rows.length === 0) { alert("No valid rows. Format: email,password,note"); return; }
      const count = await bulkAddAccounts(selectedGame, rows);
      setSuccess(`✅ ${count} accounts added!`);
      setCsvText(""); reload();
    } catch (err) { alert("Error: " + err.message); }
    finally { setSaving(false); }
  }

  return (
    <>
      <style>{S}</style>
      <div style={{ fontSize:16, fontWeight:800, color:"#fff", marginBottom:20 }}>🔑 Manage Stock</div>

      <div className="ms-grid">

        {/* ── Add accounts ── */}
        <div className="ms-card">
          <div className="ms-card-title">Add Steam Accounts</div>

          <label className="ms-lbl">Select Game *</label>
          <select className="ms-inp" style={{ cursor:"pointer" }}
            value={selectedGame} onChange={e => setSelectedGame(e.target.value)}>
            <option value="">— Choose a game —</option>
            {games.map(g => (
              <option key={g.id} value={g.id}>{g.title} (Stock: {g.stock})</option>
            ))}
          </select>

          <div className="ms-tabs">
            <button className={`ms-tab ${mode === "single" ? "on" : "off"}`} onClick={() => setMode("single")}>Single</button>
            <button className={`ms-tab ${mode === "bulk"   ? "on" : "off"}`} onClick={() => setMode("bulk")}>Bulk CSV</button>
          </div>

          {success && <div className="ms-success">{success}</div>}

          {mode === "single" ? (
            <form onSubmit={handleAddSingle}>
              <label className="ms-lbl">Steam Email *</label>
              <input className="ms-inp" value={email}    onChange={e=>setEmail(e.target.value)}    placeholder="shared@example.com" required />
              <label className="ms-lbl">Steam Password *</label>
              <input className="ms-inp" value={password} onChange={e=>setPassword(e.target.value)} placeholder="Password123" required />
              <label className="ms-lbl">Note (optional)</label>
              <input className="ms-inp" value={note}     onChange={e=>setNote(e.target.value)}     placeholder="no 2FA, region: global" />
              <button type="submit" className="ms-btn" disabled={saving||!selectedGame||!email||!password}>
                {saving ? "Adding…" : "Add Account"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleBulkAdd}>
              <label className="ms-lbl">CSV — email,password,note (one per line)</label>
              <textarea
                className="ms-inp"
                style={{ minHeight:140, resize:"vertical", fontFamily:"monospace", fontSize:12 }}
                value={csvText}
                onChange={e => setCsvText(e.target.value)}
                placeholder={"shared1@email.com,Pass123,no 2FA\nshared2@email.com,Pass456,global"}
              />
              <div style={{ fontSize:11, color:"#6b6b6b", marginBottom:12 }}>
                {csvText.trim()
                  ? `${csvText.trim().split("\n").filter(l => l.includes(",")).length} accounts detected`
                  : "Paste CSV data above"}
              </div>
              <button type="submit" className="ms-btn" disabled={saving||!selectedGame||!csvText.trim()}>
                {saving ? "Adding…" : "Bulk Add Accounts"}
              </button>
            </form>
          )}
        </div>

        {/* ── Stock overview ── */}
        <div className="ms-card">
          <div className="ms-card-title">Stock Overview</div>
          {loading ? (
            <div style={{ color:"#6b6b6b", textAlign:"center", padding:"40px 0", fontSize:13 }}>Loading…</div>
          ) : (
            games.sort((a,b) => a.stock - b.stock).map(game => (
              <div key={game.id} className="ms-stock-row">
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:"#fff" }}>{game.title}</div>
                  <div style={{ fontSize:11, color:"#6b6b6b", marginTop:2 }}>${game.price?.toFixed(2)} USD</div>
                </div>
                <div style={{ textAlign:"right" }}>
                  <div style={{
                    fontSize:22, fontWeight:800,
                    color: game.stock === 0 ? "#ef4444" : game.stock <= 2 ? "#eab308" : "#22c55e"
                  }}>{game.stock}</div>
                  <div style={{ fontSize:10, color:"#6b6b6b" }}>left</div>
                </div>
              </div>
            ))
          )}
          <div className="ms-note">
            🔒 Steam credentials are stored encrypted and only delivered via Cloud Functions after payment confirmation.
          </div>
        </div>
      </div>
    </>
  );
}